import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild, ViewEncapsulation } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl } from "@angular/forms";
import { distinctUntilChanged, filter, Observable, of, Subject, switchMap } from "rxjs";
import { NgbTypeahead } from "@ng-bootstrap/ng-bootstrap";
import { debounceTime, take } from 'rxjs/operators';
import { AnalyticsEvent, InternalAnalyticsService } from "../../core/internal-analytics.service";
import { GooglePlacesSearchService } from "./google-places-search.service";

@Component({
    selector: 'rosie-google-places-search',
    templateUrl: './google-places-search.component.html',
    styleUrls: ['./google-places-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class GooglePlacesSearchComponent implements OnInit {
    hasOptionSelected = false;
    hasSelectionError = false;
    showOptionError = false;
    searchValue = '';
    @Input() searchControl = new FormControl('');
    @Input() placeholder = 'Type your business name…';
    @Input() helperText = '';
    @Input() valueChanged: Subject<string>;
    @Input() icon = 'heroMagnifyingGlass';
    @Input() types: string[] = ['establishment'];
    @Input() searchAnalyticsEvent: AnalyticsEvent = AnalyticsEvent.GooglePlacesBusinessSearched;
    @Input() selectAnalyticsEvent: AnalyticsEvent = AnalyticsEvent.GooglePlacesBusinessSelected;
    @Input() showClearSelection = false;
    @Input() hasValue = false;
    @Input() highlight = false;
    @Input() showOptionErrorEnabled = true;
    @Input() selectionErrorMeesage = 'This Google Business Profile doesn’t have the information we need. Try searching for a different one or use your website instead.';
    // Keep what the user types even when no dropdown option is selected.
    @Input() allowFreeText = false;

    @Output() selectedPlaceDetailsChanged = new EventEmitter<any>()
    @Output() selectedPlaceIdChanged = new EventEmitter<any>()
    @Output() selectionCleared = new EventEmitter<any>()
    @Output() selectionError = new EventEmitter<any>()
    @Output() freeTextChanged = new EventEmitter<string>()

    resultFormatter = (result: any) => result.description;
    inputFormatter = (value: unknown) => this.allowFreeText && typeof value === 'string' ? value : this.resultFormatter(value);

    @ViewChild('instance', { static: true }) instance: NgbTypeahead;

    private destroyRef = inject(DestroyRef);

    constructor(private googlePlaces: GooglePlacesSearchService, private analytics: InternalAnalyticsService) {
    }

    ngOnInit(): void {
        this.valueChanged?.subscribe((value) => this.searchControl.patchValue(value));
        if (this.allowFreeText) {
            // Typed text arrives as a string; a dropdown selection arrives as the prediction object.
            this.searchControl.valueChanges.pipe(
                filter((value) => value === null || typeof value === 'string'),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe((value) => {
                this.hasOptionSelected = false;
                this.freeTextChanged.emit(value || '');
            });
        }
    }

    get isInvalid() {
        return this.searchValue && !this.hasOptionSelected;
    }

    showErrorOnBlurIfInvalid() {
        if (this.isInvalid && this.showOptionErrorEnabled) {
            this.showOptionError = true;
        }
    }

    searchFn = (text: Observable<string>) => {
        this.searchValue = '';
        this.hasSelectionError = false;
        this.showOptionError = false;
        const eventName = this.searchAnalyticsEvent || AnalyticsEvent.GooglePlacesBusinessSearched;
        return text.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            switchMap((term) => {
                this.searchValue = term;
                if (term) {
                    this.analytics.trackEvent(eventName, { query: term });
                    return new Observable<string[]>((observer) => {
                        this.googlePlaces.getPlacePredictions({ input: term, radius: 50000, types: this.types }).subscribe((predictions) => {
                            if (predictions) {
                                const sortedPredictions = predictions.sort((a, b) => a.distanceMeters - b.distanceMeters);
                                observer.next(sortedPredictions);
                            } else {
                                observer.next([]);
                            }
                            observer.complete();
                        });
                    })
                }
                return of([]);
            })
        );
    }

    handleOptionSelected(event: any): void {
        this.hasOptionSelected = true;
        this.showOptionError = false;
        // A selection ends the typing interaction — blur so the mobile keyboard
        // slides away (ENG-2201 QA).
        (document.activeElement as HTMLElement)?.blur?.();
        const selectedPrediction = event.item;
        const placeId = selectedPrediction.placeId;
        const eventName = this.selectAnalyticsEvent || AnalyticsEvent.GooglePlacesBusinessSelected;
        this.googlePlaces.selectedPlaceId.next(placeId);
        this.selectedPlaceIdChanged.emit(placeId);
        this.googlePlaces.getPlaceDetails(placeId).pipe(take(1))
            .subscribe((place) => {
                const imageUrl = place.iconUrl;
                if (imageUrl) {
                    const img = new Image();
                    img.onload = () => {
                    };
                    img.src = imageUrl;
                    place.mainIcon = imageUrl;
                }
                place.selectedPredictionText = selectedPrediction.structuredFormatting?.mainText;
                this.googlePlaces.selectedPlace.next(place);
                this.selectedPlaceDetailsChanged.emit(place);
                this.analytics.trackEvent(eventName, { selectedBusiness: place?.name });
            }, (error) => {
                this.hasSelectionError = true;
                this.selectionError.emit(this.selectionErrorMeesage);
            });
    }

    clearSelection() {
        this.selectionCleared.emit();
        this.searchControl.patchValue('');
        this.searchValue = '';
        this.showOptionError = false;
        this.showOptionError = false;
    }
}

export interface CustomerLocation {
    businessName?: string;
    businessWebsite?: string;
    city?: string;
    continent?: string;
    country?: string;
    countryCode?: string;
    region?: string;
    status?: string;
    hasGeolocationFailed?: boolean;
    query?: string;
    lat?: string;
    lon?: string;
}